import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Link2, Plus, Copy, Check, Trash2, Loader2, Users, Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'comercial', label: 'Comercial' },
  { value: 'backoffice', label: 'Backoffice' },
  { value: 'chefe_equipa', label: 'Chefe de Equipa' },
  { value: 'admin', label: 'Admin' },
  { value: 'parceiro', label: 'Parceiro' },
];

const ROLE_LABELS = {
  admin: 'Admin',
  parceiro: 'Parceiro',
  chefe_equipa: 'Chefe de Equipa',
  backoffice: 'Backoffice',
  comercial: 'Comercial',
};

export const InvitesPage = () => {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const [newInvite, setNewInvite] = useState({
    role: 'comercial',
    team_id: '',
    partner_id: '',
    team_leader_id: '',
    label: '',
    max_uses: 1,
    expires_days: '',
  });

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/invites`);
      setInvites(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const [t, p, u] = await Promise.all([
        axios.get(`${API_URL}/teams`),
        axios.get(`${API_URL}/partners`),
        axios.get(`${API_URL}/users`),
      ]);
      setTeams(t.data);
      setPartners(p.data);
      setUsers(u.data.filter(u => u.role === 'chefe_equipa'));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
    fetchOptions();
  }, [fetchInvites, fetchOptions]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { ...newInvite };
      if (!payload.team_id) delete payload.team_id;
      if (!payload.partner_id) delete payload.partner_id;
      if (!payload.team_leader_id) delete payload.team_leader_id;
      if (!payload.expires_days) delete payload.expires_days;
      
      await axios.post(`${API_URL}/invites`, payload);
      await fetchInvites();
      setCreateOpen(false);
      setNewInvite({ role: 'comercial', team_id: '', partner_id: '', team_leader_id: '', label: '', max_uses: 1, expires_days: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/invites/${id}`);
      await fetchInvites();
    } catch (err) {
      console.error(err);
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${APP_ORIGIN}/convite/${code}`);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return format(parseISO(d), "dd/MM/yy HH:mm", { locale: pt }); } catch { return d; }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="invites-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="invites-page-title">Convites</h1>
            <p className="text-slate-500 text-sm mt-1">Crie links de convite para novos membros da equipa</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchInvites} data-testid="refresh-invites-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setCreateOpen(true)} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="create-invite-btn">
              <Plus className="w-4 h-4" />
              Novo Convite
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-800">{invites.length}</div>
              <div className="text-sm text-slate-500">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-600">{invites.filter(i => i.available).length}</div>
              <div className="text-sm text-slate-500">Ativos</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{invites.reduce((acc, i) => acc + i.uses, 0)}</div>
              <div className="text-sm text-slate-500">Registos</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[140px]">Descricao</TableHead>
                  <TableHead className="min-w-[100px]">Role</TableHead>
                  <TableHead className="min-w-[120px]">Equipa</TableHead>
                  <TableHead className="min-w-[120px]">Parceiro</TableHead>
                  <TableHead className="min-w-[80px]">Usos</TableHead>
                  <TableHead className="min-w-[80px]">Estado</TableHead>
                  <TableHead className="min-w-[120px]">Criado</TableHead>
                  <TableHead className="min-w-[120px] text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : invites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Nenhum convite criado
                    </TableCell>
                  </TableRow>
                ) : (
                  invites.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invite-row-${inv.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-800">{inv.label || inv.code}</div>
                        <div className="text-xs text-slate-400 font-mono">{inv.code}</div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {ROLE_LABELS[inv.role] || inv.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{inv.team_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{inv.partner_name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{inv.uses}</span>
                        <span className="text-slate-400">/{inv.max_uses}</span>
                      </TableCell>
                      <TableCell>
                        {inv.available ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3" /> Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                            <XCircle className="w-3 h-3" /> {inv.expired ? 'Expirado' : 'Esgotado'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(inv.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyLink(inv.code)}
                            disabled={!inv.available}
                            data-testid={`copy-invite-${inv.id}`}
                          >
                            {copiedCode === inv.code ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(inv.id)}
                            data-testid={`delete-invite-${inv.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Registered users from invites */}
        {invites.some(i => i.used_by?.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Registos via Convite</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {invites.filter(i => i.used_by?.length > 0).flatMap(i => 
                  (i.used_by || []).map(u => (
                    <div key={u.user_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium text-sm text-slate-700">{u.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{u.email}</span>
                      </div>
                      <div className="text-xs text-slate-400">
                        {formatDate(u.registered_at)} | {i.label || i.code}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Invite Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md" data-testid="create-invite-dialog">
            <DialogHeader>
              <DialogTitle>Criar Convite</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Descricao (opcional)</Label>
                <Input
                  value={newInvite.label}
                  onChange={(e) => setNewInvite(p => ({...p, label: e.target.value}))}
                  placeholder="Ex: Comerciais Lisboa"
                  data-testid="invite-label-input"
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={newInvite.role} onValueChange={(v) => setNewInvite(p => ({...p, role: v}))}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Equipa</Label>
                <Select value={newInvite.team_id || "none"} onValueChange={(v) => setNewInvite(p => ({...p, team_id: v === "none" ? "" : v}))}>
                  <SelectTrigger data-testid="invite-team-select">
                    <SelectValue placeholder="Nenhuma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Parceiro</Label>
                <Select value={newInvite.partner_id || "none"} onValueChange={(v) => setNewInvite(p => ({...p, partner_id: v === "none" ? "" : v}))}>
                  <SelectTrigger data-testid="invite-partner-select">
                    <SelectValue placeholder="Nenhum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (Interno)</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {users.length > 0 && (
                <div>
                  <Label>Chefe de Equipa</Label>
                  <Select value={newInvite.team_leader_id || "none"} onValueChange={(v) => setNewInvite(p => ({...p, team_leader_id: v === "none" ? "" : v}))}>
                    <SelectTrigger data-testid="invite-leader-select">
                      <SelectValue placeholder="Nenhum" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max. Usos</Label>
                  <Input
                    type="number"
                    min={1}
                    value={newInvite.max_uses}
                    onChange={(e) => setNewInvite(p => ({...p, max_uses: parseInt(e.target.value) || 1}))}
                    data-testid="invite-max-uses-input"
                  />
                </div>
                <div>
                  <Label>Expira em (dias)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={newInvite.expires_days}
                    onChange={(e) => setNewInvite(p => ({...p, expires_days: e.target.value}))}
                    placeholder="Sem limite"
                    data-testid="invite-expires-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                data-testid="invite-create-submit-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Criar Convite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
